import { useMemo, type FC } from 'react';
import { Box, Button, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import type { EnvVar } from '@openchoreo/backstage-plugin-common';
import type { SecretOption } from '@openchoreo/backstage-design-system';
import { EnvVarEditor } from '../EnvVarEditor';
import { GroupedSection } from '../GroupedSection';
import type { UseModeStateResult } from '../../hooks/useModeState';
import type { UseEnvVarEditBufferResult } from '../../hooks/useEnvVarEditBuffer';
import {
  mergeEnvVarsWithStatus,
  type EnvVarWithStatus,
} from '../../utils/envVarUtils';
import { groupByStatus } from '../../utils/overrideGroupUtils';

const useStyles = makeStyles(theme => ({
  envVarRowWrapper: {
    marginBottom: theme.spacing(1),
  },
  inheritedRow: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
    border: `1px dashed ${theme.palette.grey[300]}`,
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  newChip: {
    height: 18,
    fontSize: 10,
    letterSpacing: '0.04em',
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
}));

export interface OverrideEnvVarListProps {
  /** Container name for tracking edit state */
  containerName: string;
  /** Override environment variables (from form state) */
  envVars: EnvVar[];
  /** Base workload environment variables (for comparison) */
  baseEnvVars: EnvVar[];
  /**
   * Override env vars as initially loaded from the binding. Used to
   * distinguish entries that were already persisted (`extra`) from
   * ones the user just added in this session (`new`).
   */
  initialEnvVars?: EnvVar[];
  /** Environment name for display in section titles (currently unused). */
  environmentName?: string;
  /** Available secrets for reference selection */
  secretOptions: SecretOption[];
  /** Plain/secret mode state manager */
  envModes: UseModeStateResult;
  /** Whether editing is disabled */
  disabled: boolean;
  /** Edit buffer state and handlers from useEnvVarEditBuffer */
  editBuffer: UseEnvVarEditBufferResult;
  /** Callback when starting to override inherited var */
  onStartOverride: (containerName: string, envVar: EnvVar) => void;
  /** Callback when env var field changes (for non-editing mode) */
  onEnvVarChange: (
    containerName: string,
    index: number,
    field: keyof EnvVar,
    value: any,
  ) => void;
  /** Callback when env var should be removed */
  onRemoveEnvVar: (containerName: string, index: number) => void;
  /** Callback when env var mode changes */
  onEnvVarModeChange: (
    containerName: string,
    index: number,
    mode: 'plain' | 'secret',
  ) => void;
  /** Callback to add new env var */
  onAddEnvVar: (containerName: string) => void;
}

/**
 * Override-aware environment variable list.
 * Shows grouped sections: Overrides, New, and Inherited (Base).
 * Displays status badges and supports inline override creation.
 */
export const OverrideEnvVarList: FC<OverrideEnvVarListProps> = ({
  containerName,
  envVars,
  baseEnvVars,
  initialEnvVars,
  // environmentName intentionally unused — section title is no longer per-env.
  secretOptions,
  envModes,
  disabled,
  editBuffer,
  onStartOverride,
  onEnvVarChange,
  onRemoveEnvVar,
  onEnvVarModeChange,
  onAddEnvVar,
}) => {
  const classes = useStyles();

  // Merge base and override env vars with status metadata
  const mergedEnvVars = useMemo(
    () => mergeEnvVarsWithStatus(baseEnvVars, envVars, initialEnvVars),
    [baseEnvVars, envVars, initialEnvVars],
  );

  // Group items by status
  const grouped = useMemo(() => groupByStatus(mergedEnvVars), [mergedEnvVars]);

  const handleAddEnvVar = () => {
    onAddEnvVar(containerName);
    const newIndex = envVars.length;
    editBuffer.startNew(containerName, newIndex);
  };

  const handleRemoveEnvVar = (index: number) => {
    // If deleting the row being edited, clear edit state first
    if (editBuffer.isRowEditing(containerName, index)) {
      editBuffer.clearEditState();
    }
    envModes.cleanupIndex(containerName, index);
    onRemoveEnvVar(containerName, index);
  };

  const handleModeChange = (index: number, mode: 'plain' | 'secret') => {
    envModes.setMode(containerName, index, mode);

    const isEditing = editBuffer.isRowEditing(containerName, index);

    if (isEditing && editBuffer.editBuffer) {
      // Update buffer when mode changes during editing
      if (mode === 'plain') {
        editBuffer.setBuffer({
          ...editBuffer.editBuffer,
          value: '',
          valueFrom: undefined,
        });
      } else {
        editBuffer.setBuffer({
          ...editBuffer.editBuffer,
          value: undefined,
          valueFrom: { secretKeyRef: { name: '', key: '' } },
        });
      }
    } else {
      // Update parent state directly (non-editing mode changes)
      onEnvVarModeChange(containerName, index, mode);
    }
  };

  // Handle starting override of an inherited env var
  const handleStartOverride = (envVar: EnvVar) => {
    onStartOverride(containerName, envVar);
    // After override is added, set it to editing mode with buffer initialized
    const newIndex = envVars.length;
    // Initialize buffer with a copy of the base env var
    editBuffer.startNew(containerName, newIndex, envVar);
  };

  // Render an editable env var row (overridden or new)
  const renderEditableRow = (item: EnvVarWithStatus, displayIndex: number) => {
    const actualIndex = item.actualIndex!;
    const isCurrentlyEditing = editBuffer.isRowEditing(
      containerName,
      actualIndex,
    );
    const currentMode = envModes.getMode(containerName, actualIndex);

    return (
      <Box
        key={`${item.status}-${item.envVar.key}-${displayIndex}`}
        className={classes.envVarRowWrapper}
      >
        {item.status === 'new' && (
          <Box className={classes.rowHeader}>
            <Chip
              size="small"
              label="NEW"
              color="primary"
              className={classes.newChip}
            />
          </Box>
        )}
        <EnvVarEditor
          envVar={
            isCurrentlyEditing && editBuffer.editBuffer
              ? editBuffer.editBuffer
              : item.envVar
          }
          secrets={secretOptions}
          disabled={disabled}
          mode={currentMode}
          isEditing={isCurrentlyEditing}
          onEdit={() => editBuffer.startEdit(containerName, actualIndex)}
          onApply={editBuffer.applyEdit}
          onCancel={editBuffer.cancelEdit}
          editButtonLabel="Edit"
          lockMode={item.status === 'overridden'}
          lockKey={item.status === 'overridden'}
          applyDisabled={isCurrentlyEditing && !editBuffer.isBufferValid}
          editDisabled={editBuffer.isAnyRowEditing && !isCurrentlyEditing}
          deleteDisabled={editBuffer.isAnyRowEditing && !isCurrentlyEditing}
          baseValue={item.baseValue}
          onChange={
            isCurrentlyEditing
              ? editBuffer.updateBuffer
              : (field, value) =>
                  onEnvVarChange(containerName, actualIndex, field, value)
          }
          onRemove={() => handleRemoveEnvVar(actualIndex)}
          onModeChange={mode => handleModeChange(actualIndex, mode)}
        />
      </Box>
    );
  };

  // Render an inherited (read-only) env var row using EnvVarEditor
  const renderInheritedRow = (item: EnvVarWithStatus) => {
    const baseMode = item.envVar.valueFrom?.secretKeyRef ? 'secret' : 'plain';
    return (
      <Box
        key={`inherited-${item.envVar.key}`}
        className={classes.envVarRowWrapper}
      >
        <EnvVarEditor
          envVar={item.envVar}
          secrets={secretOptions}
          disabled={disabled}
          mode={baseMode}
          isEditing={false}
          onEdit={() => handleStartOverride(item.envVar)}
          onApply={() => {}}
          editButtonLabel="Override"
          editDisabled={editBuffer.isAnyRowEditing}
          hideDelete
          className={classes.inheritedRow}
          onChange={() => {}}
          onRemove={() => {}}
          onModeChange={() => {}}
        />
      </Box>
    );
  };

  return (
    <Box>
      {/* Not in current workload — overrides whose key isn't in the bound release's workload.
          Includes both stale entries already on the binding ('extra') and ones the user
          just added in this form session ('new'). 'new' rows show a NEW chip. */}
      {grouped.extra.length + grouped.new.length > 0 && (
        <GroupedSection
          title="Not in workload"
          titleTooltip="These overrides have no matching variable in the release's workload spec. They'll still be applied to the environment binding when you save. Common when a previous release had this variable, or when you've just added a new one."
          count={grouped.extra.length + grouped.new.length}
          status="new"
          defaultExpanded
        >
          {[...grouped.extra, ...grouped.new].map((item, index) =>
            renderEditableRow(item as EnvVarWithStatus, index),
          )}
        </GroupedSection>
      )}

      {/* Overrides section */}
      {grouped.overridden.length > 0 && (
        <GroupedSection
          title="Overrides"
          count={grouped.overridden.length}
          status="overridden"
          defaultExpanded
        >
          {grouped.overridden.map((item, index) =>
            renderEditableRow(item as EnvVarWithStatus, index),
          )}
        </GroupedSection>
      )}

      {/* From Workload Config section */}
      {grouped.inherited.length > 0 && (
        <GroupedSection
          title="From Workload Config"
          count={grouped.inherited.length}
          status="inherited"
          defaultExpanded
        >
          {grouped.inherited.map(item =>
            renderInheritedRow(item as EnvVarWithStatus),
          )}
        </GroupedSection>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddEnvVar}
        variant="outlined"
        size="small"
        className={classes.addButton}
        disabled={disabled || editBuffer.isAnyRowEditing}
        color="primary"
      >
        Add Environment Variable
      </Button>
    </Box>
  );
};
